import React, { useState } from 'react'
import Container from '@cloudscape-design/components/container'
import Header from '@cloudscape-design/components/header'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Button from '@cloudscape-design/components/button'
import Box from '@cloudscape-design/components/box'
import type { BenchmarkRun, AnalysisRequest } from '@/types'
import { useAppStore } from '@/store/app-store'
import { requestAnalysis } from '@/engine/api-client'

type Priority = 'speed' | 'cost' | 'quality'

interface Props {
  run: BenchmarkRun | null
}

export function buildAnalysisRequest(run: BenchmarkRun): AnalysisRequest {
  const stats = run.stats
  const modelNames = run.stats.map(s => s.profileName)
  const prompt = run.profiles[0]?.prompt?.slice(0, 500) || ''
  const successCount = run.results.filter(r => r.success).length
  const failCount = run.results.filter(r => !r.success).length
  const times = run.results.filter(r => r.success).map(r => r.totalTimeSec)
  const minTime = times.length > 0 ? Math.min(...times) : 0
  const maxTime = times.length > 0 ? Math.max(...times) : 0
  const results = `${successCount} successful, ${failCount} failed. ` +
    `Min time: ${minTime.toFixed(3)}s, Max time: ${maxTime.toFixed(3)}s.`

  return { stats, prompt, modelNames, results }
}

/** Lightweight inline markdown renderer */
export function renderMarkdown(text: string): React.ReactNode[] {
  const lines = text.split('\n')
  const elements: React.ReactNode[] = []
  let bulletBuffer: React.ReactNode[] = []
  let key = 0

  const flushBullets = () => {
    if (bulletBuffer.length > 0) {
      elements.push(<ul key={key++} style={{ margin: '4px 0', paddingLeft: 20 }}>{bulletBuffer}</ul>)
      bulletBuffer = []
    }
  }

  const formatInline = (line: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = []
    const regex = /(\*\*(.+?)\*\*|`([^`]+)`)/g
    let lastIndex = 0
    let match: RegExpExecArray | null
    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIndex) parts.push(line.slice(lastIndex, match.index))
      if (match[2]) parts.push(<strong key={`b${match.index}`}>{match[2]}</strong>)
      else if (match[3]) parts.push(<code key={`c${match.index}`} style={{ padding: '1px 4px', borderRadius: 3, fontSize: 12 }}>{match[3]}</code>)
      lastIndex = regex.lastIndex
    }
    if (lastIndex < line.length) parts.push(line.slice(lastIndex))
    return parts
  }

  for (const line of lines) {
    if (line.startsWith('### ')) {
      flushBullets()
      elements.push(<h3 key={key++} style={{ fontSize: 14, margin: '12px 0 6px' }}>{formatInline(line.slice(4))}</h3>)
    } else if (line.startsWith('## ')) {
      flushBullets()
      elements.push(<h2 key={key++} style={{ fontSize: 16, margin: '16px 0 8px' }}>{formatInline(line.slice(3))}</h2>)
    } else if (line.startsWith('- ')) {
      bulletBuffer.push(<li key={key++} style={{ margin: '2px 0' }}>{formatInline(line.slice(2))}</li>)
    } else if (line.trim() === '') {
      flushBullets()
      elements.push(<br key={key++} />)
    } else {
      flushBullets()
      elements.push(<p key={key++} style={{ margin: '4px 0' }}>{formatInline(line)}</p>)
    }
  }
  flushBullets()
  return elements
}

export function AnalysisPanel({ run }: Props) {
  const [priority, setPriority] = useState<Priority>('speed')
  const analysisText = useAppStore(s => s.analysisText)
  const analysisLoading = useAppStore(s => s.analysisLoading)
  const analysisError = useAppStore(s => s.analysisError)
  const setAnalysisText = useAppStore(s => s.setAnalysisText)
  const setAnalysisLoading = useAppStore(s => s.setAnalysisLoading)
  const setAnalysisError = useAppStore(s => s.setAnalysisError)

  const handleGenerate = async () => {
    if (!run) return
    setAnalysisLoading(true)
    setAnalysisError(null)
    try {
      const req = buildAnalysisRequest(run)
      req.priority = priority
      const analysis = await requestAnalysis(req)
      setAnalysisText(analysis)
    } catch (err: any) {
      setAnalysisError(err.message || 'Analysis generation failed.')
    } finally {
      setAnalysisLoading(false)
    }
  }

  const disabled = !run || analysisLoading

  const priorities: { key: Priority; label: string; icon: string }[] = [
    { key: 'speed', label: 'Speed', icon: '⚡' },
    { key: 'cost', label: 'Cost', icon: '💰' },
    { key: 'quality', label: 'Quality', icon: '✨' }
  ]

  return (
    <Container
      header={
        <Header
          actions={
            <Button
              variant="primary"
              disabled={disabled}
              loading={analysisLoading}
              onClick={handleGenerate}
            >
              Generate Analysis
            </Button>
          }
        >
          AI Analysis
        </Header>
      }
    >
      <SpaceBetween size="m">
        <SpaceBetween size="xs">
          <Box variant="small" color="text-body-secondary">What matters most to you?</Box>
          <SpaceBetween direction="horizontal" size="xs">
            {priorities.map(p => (
              <Button
                key={p.key}
                variant={priority === p.key ? 'primary' : 'normal'}
                onClick={() => setPriority(p.key)}
              >
                {p.icon} {p.label}
              </Button>
            ))}
          </SpaceBetween>
        </SpaceBetween>

        {!run && (
          <Box textAlign="center" color="text-body-secondary" padding="l">
            Run a benchmark to generate an AI analysis
          </Box>
        )}

        {analysisLoading && (
          <Box color="text-status-info">⏳ Generating analysis…</Box>
        )}

        {analysisError && (
          <Box color="text-status-error">{analysisError}</Box>
        )}

        {analysisText && (
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>
            {renderMarkdown(analysisText)}
          </div>
        )}
      </SpaceBetween>
    </Container>
  )
}
